import {
  Link as RouterLink,
  LinkProps as RouterLinkProps,
} from "react-router-dom";
import { useCloseActiveDrawer } from "@/store/selectors";
import { useMemo } from "react";

export interface LinkProps extends RouterLinkProps {
  disabled?: boolean;
  radius?: string | number;
  ariaLabel?: string;
  keepExistingUrlParams?: boolean;
}

export const Link = (props: LinkProps) => {
  const {
    to,
    children,
    disabled,
    onClick,
    radius,
    ariaLabel,
    keepExistingUrlParams,
    ...rest
  } = props;
  const closeActiveDrawer = useCloseActiveDrawer();

  const toWithUrlParams = useMemo(() => {
    if (!keepExistingUrlParams) {
      return to;
    }

    const toString = typeof to === "string" ? to : to.pathname || "";

    // Only preserve params if there are existing ones
    if (!window.location.search) {
      return to;
    }

    const currentUrlParams = new URLSearchParams(window.location.search);
    const newUrl = new URL(toString, window.location.origin);
    newUrl.search = currentUrlParams.toString();
    return newUrl.toString();
  }, [to, keepExistingUrlParams]);

  if (disabled) {
    return <span aria-disabled>{children}</span>;
  }

  return (
    <RouterLink
      to={toWithUrlParams}
      aria-disabled={disabled}
      aria-label={
        typeof children === "string"
          ? children || ariaLabel
          : ariaLabel || "click link"
      }
      style={{
        pointerEvents: disabled ? "none" : undefined,
        borderRadius: radius,
      }}
      onClick={(e) => {
        closeActiveDrawer();
        onClick?.(e);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          closeActiveDrawer();
          onClick?.(e as any);
        }
      }}
      {...rest}
    >
      {children}
    </RouterLink>
  );
};
